import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuthCtx } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { seo } from "@/lib/seo";
import { CardFormSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/reset-password")({
  head: () =>
    seo({
      path: "/reset-password",
      title: "Redefinir senha — Seu Bolso Tranquilo",
      description: "Defina uma nova senha para sua conta.",
      noindex: true,
    }),
  component: ResetPasswordPage,
  pendingComponent: () => <CardFormSkeleton fields={2} />,
});

const schema = z
  .object({
    password: z
      .string()
      .min(8, "Use pelo menos 8 caracteres")
      .max(128, "Máximo de 128 caracteres")
      .regex(/[A-Za-z]/, "Inclua pelo menos uma letra")
      .regex(/\d/, "Inclua pelo menos um número"),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    message: "As senhas não coincidem",
    path: ["confirm"],
  });

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { updatePassword } = useAuthCtx();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Supabase puts the recovery tokens in the URL hash (#access_token=...&type=recovery)
  // The client auto-processes them; we just confirm a recovery session exists.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      const hash = typeof window !== "undefined" ? window.location.hash : "";
      if (data.session || hash.includes("type=recovery") || hash.includes("access_token")) {
        setReady(true);
      } else {
        setError("Link inválido ou expirado. Solicite um novo link de redefinição.");
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const parsed = schema.safeParse({ password, confirm });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Verifique os campos.");
      return;
    }
    setSubmitting(true);
    try {
      const { error: updateError } = await updatePassword(parsed.data.password);
      if (updateError) {
        const msg = (updateError.message ?? "").toLowerCase();
        if (
          msg.includes("expired") ||
          msg.includes("invalid") ||
          msg.includes("token") ||
          msg.includes("session")
        ) {
          setError(
            "Seu link de redefinição é inválido ou expirou. Solicite um novo link para continuar.",
          );
        } else {
          toast.error("Não foi possível atualizar a senha. Tente novamente.");
        }
        return;
      }
      toast.success("Senha atualizada com sucesso!");
      setSuccess(true);
    } finally {
      setSubmitting(false);
    }
  };

  const goToLogin = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
    } catch {
      // ignora — apenas garantimos sessão limpa antes de ir para o login
    }
    navigate({ to: "/auth" });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/auth"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="size-4" /> Voltar para login
        </Link>

        <div className="rounded-3xl border border-border/40 bg-gradient-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <BrandLogo className="size-10" />
            <div>
              <h1 className="font-display text-2xl font-bold leading-tight">Nova senha</h1>
              <p className="text-sm text-muted-foreground">Defina uma nova senha para entrar.</p>
            </div>
          </div>

          {success ? (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary/10">
                <CheckCircle2 className="size-8 text-primary" />
              </div>
              <div className="space-y-2">
                <h2 className="font-display text-xl font-semibold">Senha atualizada!</h2>
                <p className="text-sm text-muted-foreground">
                  Sua nova senha já está ativa. O pedido enviado por email foi processado com
                  sucesso — agora é só entrar com a senha nova.
                </p>
              </div>
              <button
                type="button"
                onClick={goToLogin}
                disabled={signingOut}
                aria-busy={signingOut}
                className="block w-full py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {signingOut && <Loader2 className="size-4 animate-spin" />}
                Ir para o login
              </button>
            </div>
          ) : error ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4">
                <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-destructive">Link inválido ou expirado</p>
                  <p className="text-xs text-muted-foreground">{error}</p>
                </div>
              </div>
              <Link
                to="/esqueci-senha"
                className="block text-center w-full py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity"
              >
                Voltar para “Esqueci minha senha”
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" aria-busy={submitting}>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Nova senha</label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    maxLength={128}
                    className="w-full px-4 py-3 pr-12 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                    aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Confirmar senha</label>
                <input
                  type={showPwd ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  maxLength={128}
                  className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !ready}
                aria-busy={submitting}
                className="w-full py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="size-4 animate-spin" />}
                {submitting ? "Atualizando..." : "Atualizar senha"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}